"use client";
import {Popover} from "@headlessui/react";
import {useState} from "react";
import {FaCheck, FaTimes, FaInfoCircle} from "react-icons/fa";

type Plano = "basico" | "profissional" | "ultra";
type Period = "monthly" | "quarterly" | "semi_annual" | "yearly";

const CheckIcon = () => <FaCheck className='shrink-0 fill-purple-500 mr-3'/>;
const TimesIcon = () => <FaTimes className='shrink-0 fill-red-500 mr-3'/>;

// Componente auxiliar para os títulos (com ou sem popover)
const HeaderTitle = ({title, popoverContent}: { title: string; popoverContent?: string }) => {
    return (
        <div className='px-6 flex flex-col justify-end'>
            {popoverContent ? (
                <div className='flex items-center justify-between py-3 text-slate-400 border-slate-800 border-b-2'>
                    <span className='leading-[1.2]'>{title}</span>
                    <Popover className='relative'>
                        <Popover.Button
                            className='text-slate-500 hover:text-white focus:outline-none flex items-center'>
                            <FaInfoCircle className='w-4 h-4 ml-4'/>
                        </Popover.Button>
                        <Popover.Panel
                            className='absolute z-10 p-3 w-64 bg-slate-800 text-sm text-slate-300 rounded-lg shadow-lg right-0'>
                            {popoverContent}
                        </Popover.Panel>
                    </Popover>
                </div>
            ) : (
                <div className='py-2 text-slate-400 border-slate-800 border-b-2'>{title}</div>
            )}
        </div>
    );
};

const FeatureItem = ({name, iconType}: { name: string; iconType: "check" | "times" }) => {
    const IconComponent = iconType === "check" ? CheckIcon : TimesIcon;

    return (
        <div className='px-6 flex flex-col justify-end'>
            <div className='flex items-center h-full py-2 text-slate-400 border-b border-slate-800'>
                <IconComponent/>
                <span>
          <span className='md:hidden'>{name}</span>
        </span>
            </div>
        </div>
    );
};

// Novo componente para mostrar as características no mobile com popover
// Versão final do componente MobileFeatureInfo com correção para o popover do último item
const MobileFeatureInfo = ({
                               title,
                               popoverContent,
                               iconType,
                           }: {
    title: string;
    popoverContent?: string;
    iconType: "check" | "times";
}) => {
    const IconComponent = iconType === "check" ? CheckIcon : TimesIcon;

    return (
        <div className='px-6 py-2 border-b border-slate-800'>
            <div className='flex items-center justify-between text-slate-400'>
                <div className='flex items-center'>
                    <IconComponent/>
                    <span>{title}</span>
                </div>
                {popoverContent && (
                    <Popover className='relative'>
                        <Popover.Button
                            className='text-slate-500 hover:text-white focus:outline-none flex items-center'>
                            <FaInfoCircle className='w-4 h-4 ml-2'/>
                        </Popover.Button>
                        <Popover.Panel
                            className='absolute z-50 p-3 w-64 bg-slate-800 text-sm text-slate-300 rounded-lg shadow-lg bottom-8 right-0'>
                            {popoverContent}
                        </Popover.Panel>
                    </Popover>
                )}
            </div>
        </div>
    );
};

export default function Pricing() {
    const [selectedPeriod, setSelectedPeriod] = useState<Period>("monthly");

    const getPeriodLabel = (period: Period): string => {
        const labels: { [key in Period]: string } = {
            monthly: "mês",
            quarterly: "trimestre",
            semi_annual: "semestre",
            yearly: "ano"
        };
        return labels[period];
    };

    const getDiscountMessage = (): string => {
        return selectedPeriod === 'monthly'
            ? 'Sem desconto neste modelo - Upgrade para economizar!'
            : 'Economize 16,67% com pagamento antecipado em planos acima de mensal';
    };

    const plans = {
        basico: {
            monthly: "129.00",
            quarterly: "322.00",
            semi_annual: "645.00",
            yearly: "1290.00"
        },
        profissional: {
            monthly: "219.00",
            quarterly: "547.00",
            semi_annual: "1095.00",
            yearly: "2190.00"
        },
        ultra: {
            monthly: "299.00",
            quarterly: "747.00",
            semi_annual: "1495.00",
            yearly: "2990.00"
        }
    };

    const sendMessageToWhatsApp = (plano: Plano) => {
        const period = getPeriodLabel(selectedPeriod);
        const valor = plans[plano][selectedPeriod];
        const message = `Olá, estou interessado no plano ${plano.toUpperCase()} no valor de R$${valor} (${period}).`;

        const whatsappURL = `https://api.whatsapp.com/send?phone=5511940590576&text=${encodeURIComponent(message)}`;
        window.open(whatsappURL, "_blank");
    };

    return (
        <section className='relative' id='plans'>
            {/* Radial gradient */}
            <div className='absolute inset-0 overflow-hidden pointer-events-none -z-10' aria-hidden='true'>
                <div
                    className='absolute flex items-center justify-center top-0 -translate-y-1/2 left-1/2 -translate-x-1/2 w-1/3 aspect-square'>
                    <div className='absolute inset-0 translate-z-0 bg-purple-500 rounded-full blur-[120px] opacity-50'/>
                </div>
            </div>
            <div className='max-w-6xl mx-auto px-4 sm:px-6'>
                <div className='py-12 md:py-20'>
                    {/* Content */}
                    <div className='max-w-3xl mx-auto text-center pb-12 md:pb-20'>
                        <div>
                            <div
                                className='inline-flex font-medium bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-purple-200 pb-3'>
                                Preços dos planos
                            </div>
                        </div>
                        <h2 className='h2 bg-clip-text text-transparent bg-gradient-to-r from-slate-200/60 via-slate-200 to-slate-200/60 pb-4'>
                            Planos Rota Lounge
                        </h2>
                        <p className='text-lg text-slate-400'>
                            Escolha o plano perfeito para o seu negócio. Do básico ao Ultra, oferecemos soluções
                            adaptáveis às suas
                            necessidades para uma gestão mais eficiente e resultados imediatos. Com planos mensais e
                            anuais que cabem
                            no seu bolso – e no anual, você ainda ganha 2 meses grátis para economizar mais.
                        </p>
                    </div>

                    {/* Desktop Version */}
                    <div className='relative max-md:hidden'>
                        {/* Blurred shape */}
                        <div
                            className='absolute bottom-0 -mb-20 left-2/3 -translate-x-1/2 blur-2xl opacity-70 pointer-events-none'
                            aria-hidden='true'
                        >
                            <svg xmlns='http://www.w3.org/2000/svg' width='434' height='427'>
                                <defs>
                                    <linearGradient id='bs5-a' x1='19.609%' x2='50%' y1='14.544%' y2='100%'>
                                        <stop offset='0%' stopColor='#A855F7'/>
                                        <stop offset='100%' stopColor='#6366F1' stopOpacity='0'/>
                                    </linearGradient>
                                </defs>
                                <path
                                    fill='url(#bs5-a)'
                                    fillRule='evenodd'
                                    d='m661 736 461 369-284 58z'
                                    transform='matrix(1 0 0 -1 -661 1163)'
                                />
                            </svg>
                        </div>
                        {/* Content */}
                        <div
                            className='grid md:grid-cols-4 xl:-mx-6 text-sm [&>div:nth-of-type(-n+4)]:py-6 [&>div:nth-last-of-type(-n+4)]:pb-6 max-md:[&>div:nth-last-of-type(-n+4)]:mb-8 max-md:[&>div:nth-of-type(-n+4):nth-of-type(n+1)]:rounded-t-3xl max-md:[&>div:nth-last-of-type(-n+4)]:rounded-b-3xl md:[&>div:nth-of-type(2)]:rounded-tl-3xl md:[&>div:nth-of-type(4)]:rounded-tr-3xl md:[&>div:nth-last-of-type(3)]:rounded-bl-3xl md:[&>div:nth-last-of-type(1)]:rounded-br-3xl [&>div]:bg-slate-700/20 [&>div:nth-of-type(4n+1)]:bg-transparent max-md:[&>div:nth-of-type(4n+5)]:hidden max-md:[&>div:nth-of-type(4n+2)]:order-1 max-md:[&>div:nth-of-type(4n+3)]:order-2 max-md:[&>div:nth-of-type(4n+4)]:order-3 max-md:md:[&>div:nth-of-type(n)]:mb-0 [&>div:nth-of-type(4n+3)]:relative before:[&>div:nth-of-type(4n+3)]:absolute before:[&>div:nth-of-type(4n+3)]:-inset-px before:[&>div:nth-of-type(4n+3)]:rounded-[inherit] before:[&>div:nth-of-type(4n+3)]:border-x-2 before:[&>div:nth-of-type(3)]:border-t-2 before:[&>div:nth-last-of-type(2)]:border-b-2 before:[&>div:nth-of-type(4n+3)]:border-purple-500 before:[&>div:nth-of-type(4n+3)]:-z-10 before:[&>div:nth-of-type(4n+3)]:pointer-events-none'>
                            {/* Pricing toggle */}
                            <div className='px-6 flex flex-col justify-end'>
                                <div className='pb-5 md:border-b border-slate-800'>
                                    {/* Toggle switch */}
                                    <div className='text-center'>
                                        <div className='flex flex-col items-center'>
                                            <div className='text-sm text-center mb-3'>
                        <span className='text-teal-500'>
                          {getDiscountMessage()}
                        </span>
                                            </div>

                                            {/* Radio buttons para seleção de período */}
                                            <div className='flex flex-wrap justify-center gap-2 text-xs'>
                                                <label className='flex items-center cursor-pointer'>
                                                    <input
                                                        type='radio'
                                                        name='period'
                                                        value='monthly'
                                                        checked={selectedPeriod === 'monthly'}
                                                        onChange={(e) => setSelectedPeriod(e.target.value as Period)}
                                                        className='sr-only peer'
                                                    />
                                                    <div className={`px-2 py-1 rounded-md border transition-colors ${
                                                        selectedPeriod === 'monthly'
                                                            ? 'bg-purple-500 text-white border-purple-500'
                                                            : 'bg-slate-700 text-slate-300 border-slate-600 hover:border-purple-400'
                                                    }`}>
                                                        Mensal
                                                    </div>
                                                </label>

                                                <label className='flex items-center cursor-pointer'>
                                                    <input
                                                        type='radio'
                                                        name='period'
                                                        value='quarterly'
                                                        checked={selectedPeriod === 'quarterly'}
                                                        onChange={(e) => setSelectedPeriod(e.target.value as Period)}
                                                        className='sr-only peer'
                                                    />
                                                    <div className={`px-2 py-1 rounded-md border transition-colors ${
                                                        selectedPeriod === 'quarterly'
                                                            ? 'bg-purple-500 text-white border-purple-500'
                                                            : 'bg-slate-700 text-slate-300 border-slate-600 hover:border-purple-400'
                                                    }`}>
                                                        Trimestral
                                                    </div>
                                                </label>

                                                <label className='flex items-center cursor-pointer'>
                                                    <input
                                                        type='radio'
                                                        name='period'
                                                        value='semi_annual'
                                                        checked={selectedPeriod === 'semi_annual'}
                                                        onChange={(e) => setSelectedPeriod(e.target.value as Period)}
                                                        className='sr-only peer'
                                                    />
                                                    <div className={`px-2 py-1 rounded-md border transition-colors ${
                                                        selectedPeriod === 'semi_annual'
                                                            ? 'bg-purple-500 text-white border-purple-500'
                                                            : 'bg-slate-700 text-slate-300 border-slate-600 hover:border-purple-400'
                                                    }`}>
                                                        Semestral
                                                    </div>
                                                </label>

                                                <label className='flex items-center cursor-pointer'>
                                                    <input
                                                        type='radio'
                                                        name='period'
                                                        value='yearly'
                                                        checked={selectedPeriod === 'yearly'}
                                                        onChange={(e) => setSelectedPeriod(e.target.value as Period)}
                                                        className='sr-only peer'
                                                    />
                                                    <div className={`px-2 py-1 rounded-md border transition-colors ${
                                                        selectedPeriod === 'yearly'
                                                            ? 'bg-purple-500 text-white border-purple-500'
                                                            : 'bg-slate-700 text-slate-300 border-slate-600 hover:border-purple-400'
                                                    }`}>
                                                        Anual
                                                    </div>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Plano Basico*/}
                            <div className='px-6 flex flex-col justify-end'>
                                <div className='grow pb-4 mb-4 border-b border-slate-800'>
                                    <div
                                        className='text-base font-medium bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-purple-200 pb-0.5'>
                                        Plano Básico
                                    </div>
                                    <div className='mb-1'>
                                        <span className='text-lg font-medium text-slate-500 pr-1'>R$</span>
                                        <span
                                            className='text-3xl font-bold text-slate-50'>{plans.basico[selectedPeriod]}</span>/
                                        <span
                                            className='text-sm text-slate-600 font-medium pl-1'>{getPeriodLabel(selectedPeriod)}</span>
                                    </div>
                                    <div className='text-slate-500 text-center'>Para Pequenos Negócios Até 2
                                        Funcionários
                                    </div>
                                </div>
                                <div className='pb-4 border-b border-slate-800'>
                                    <button
                                        onClick={() => sendMessageToWhatsApp("basico")}
                                        className='btn-sm text-slate-900 bg-gradient-to-r from-white/80 via-white to-white/80 hover:bg-white w-full transition-transform duration-150 ease-in-out group rounded-ss-lg hover:scale-105'
                                    >
                                        Assinar Básico
                                        <span
                                            className='tracking-normal text-purple-500 group-hover:translate-x-0.5 transition-transform duration-150 ease-in-out ml-1'>
                      ➤
                    </span>
                                    </button>
                                </div>
                            </div>
                            {/* Plano Profissional */}
                            <div className='px-6 flex flex-col justify-end'>
                                <div className='grow pb-4 mb-4 border-b border-slate-800'>
                                    <div
                                        className='text-base font-medium bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-purple-200 pb-0.5'>
                                        Plano Profissional
                                    </div>
                                    <div className='mb-1'>
                                        <span className='text-lg font-medium text-slate-500 pr-1'>R$</span>
                                        <span
                                            className='text-3xl font-bold text-slate-50'>{plans.profissional[selectedPeriod]}</span>/
                                        <span
                                            className='text-sm text-slate-600 font-medium pl-1'>{getPeriodLabel(selectedPeriod)}</span>
                                    </div>
                                    <div className='text-slate-500 text-center'>Para Negócios Com Alto Volume de
                                        Vendas
                                    </div>
                                </div>
                                <div className='pb-4 border-b border-slate-800'>
                                    <button
                                        onClick={() => sendMessageToWhatsApp("profissional")}
                                        className='btn-sm text-slate-900 bg-gradient-to-r from-white/80 via-white to-white/80 hover:bg-white w-full transition-transform duration-150 ease-in-out group rounded-ss-lg hover:scale-105'
                                    >
                                        Assinar Profissional
                                        <span
                                            className='tracking-normal text-purple-500 group-hover:translate-x-0.5 transition-transform duration-150 ease-in-out ml-1'>
                      ➤
                    </span>
                                    </button>
                                </div>
                            </div>
                            {/* Plano Ultra */}
                            <div className='px-6 flex flex-col justify-end'>
                                <div className='grow pb-4 mb-4 border-b border-slate-800'>
                                    <div
                                        className='text-base font-medium bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-purple-200 pb-0.5'>
                                        Plano Ultra
                                    </div>
                                    <div className='mb-1'>
                                        <span className='text-lg font-medium text-slate-500 pr-1'>R$</span>
                                        <span
                                            className='text-3xl font-bold text-slate-50'>{plans.ultra[selectedPeriod]}</span>/
                                        <span
                                            className='text-sm text-slate-600 font-medium pl-1'>{getPeriodLabel(selectedPeriod)}</span>
                                    </div>
                                    <div className='text-slate-500 text-center'>Para Grandes Empresas Operando em
                                        Múltiplas Frentes
                                    </div>
                                </div>
                                <div className='pb-4 border-b border-slate-800'>
                                    <button
                                        onClick={() => sendMessageToWhatsApp("ultra")}
                                        className='btn-sm text-slate-900 bg-gradient-to-r from-white/80 via-white to-white/80 hover:bg-white w-full transition-transform duration-150 ease-in-out group rounded-ss-lg hover:scale-105'
                                    >
                                        Assinar Ultra
                                        <span
                                            className='tracking-normal text-purple-500 group-hover:translate-x-0.5 transition-transform duration-150 ease-in-out ml-1'>
                      ➤
                    </span>
                                    </button>
                                </div>
                            </div>
                            {/* # Usage */}
                            <div className='px-6 flex flex-col justify-end'>
                                <div className='py-2 text-slate-50 font-medium mt-4'>Serviços</div>
                            </div>
                            <div className='px-6 flex flex-col justify-end'>
                                <div className='py-2 text-slate-50 font-medium mt-4 md:hidden'>Serviços</div>
                            </div>
                            <div className='px-6 flex flex-col justify-end'>
                                <div className='py-2 text-slate-50 font-medium mt-4 md:hidden'>Serviços</div>
                            </div>
                            <div className='px-6 flex flex-col justify-end'>
                                <div className='py-2 text-slate-50 font-medium mt-4 md:hidden'>Serviços</div>
                            </div>
                            {/* Atualizações em Tempo Real */}
                            <HeaderTitle
                                title='Atualizações em Tempo Real'
                                popoverContent='Inclui 2.000 requisições diárias para atualizações em tempo real. Acima desse limite, custos adicionais podem ser aplicados.'
                            />
                            <FeatureItem name='Atualizações em Tempo Real' iconType='check'/>
                            <FeatureItem name='Atualizações em Tempo Real' iconType='check'/>
                            <FeatureItem name='Atualizações em Tempo Real' iconType='check'/>
                            {/* Vendas PDV */}
                            <HeaderTitle
                                title='Vendas PDV'
                                popoverContent='Realize vendas rápidas no balcão com um PDV de interface simples e intuitiva, ideal para agilizar vendas.'
                            />
                            <FeatureItem name='Vendas PDV' iconType='check'/>
                            <FeatureItem name='Vendas PDV' iconType='check'/>
                            <FeatureItem name='Vendas PDV' iconType='check'/>
                            {/* Estoque */}
                            <HeaderTitle
                                title='Estoque'
                                popoverContent='Gerencie seu estoque de forma integrada ao módulo de produtos, permitindo adicionar, remover e ajustar quantidades com facilidade para manter o controle total dos itens.'
                            />
                            <FeatureItem name='Estoque' iconType='check'/>
                            <FeatureItem name='Estoque' iconType='check'/>
                            <FeatureItem name='Estoque' iconType='check'/>
                            {/* Clientes */}
                            <HeaderTitle
                                title='Clientes'
                                popoverContent='Gerencie seus clientes com facilidade, cadastrando-os manualmente ou automaticamente via página de pedidos online, e tenha controle total sobre as informações e histórico de cada cliente.'
                            />
                            <FeatureItem name='Clientes' iconType='check'/>
                            <FeatureItem name='Clientes' iconType='check'/>
                            <FeatureItem name='Clientes' iconType='check'/>
                            {/* Criação de Produtos Simples */}
                            <HeaderTitle
                                title='Criação de Produtos Simples'
                                popoverContent='Cadastre produtos definindo preços, categorias e descrições de forma rápida e prática, '
                            />
                            <FeatureItem name='Criação de Produtos Simples' iconType='check'/>
                            <FeatureItem name='Criação de Produtos Simples' iconType='check'/>
                            <FeatureItem name='Criação de Produtos Simples' iconType='check'/>
                            {/* Criação de Produtos Compostos (Kit Combo) */}
                            <HeaderTitle
                                title='Criação de Produtos Compostos'
                                popoverContent='Monte combos e kits de produtos de maneira simpes e fácil.'
                            />
                            <FeatureItem name='Criação de Produtos Compostos' iconType='times'/>
                            <FeatureItem name='Criação de Produtos Compostos' iconType='check'/>
                            <FeatureItem name='Criação de Produtos Compostos' iconType='check'/>
                            {/* Comandas */}
                            <HeaderTitle
                                title='Comandas'
                                popoverContent='Melhore o atendimento com comandas digitais, eliminando erros e otimizando o fluxo de pedidos tanto no balcão quanto pelos garçons, para uma operação ágil e eficiente.'
                            />
                            <FeatureItem name='Comandas' iconType='times'/>
                            <FeatureItem name='Comandas' iconType='check'/>
                            <FeatureItem name='Comandas' iconType='check'/>
                            {/* Menu Online via QRCode */}
                            <HeaderTitle
                                title='Menu Online via QRCode'
                                popoverContent='Disponibilizamos um menu online via QRCode, integrado às comandas, para que seus clientes visualizem os produtos disponíveis ao escanear o código no estabelecimento. Ideal para ser impresso e fixado nas mesas, permitindo que o cliente confira o cardápio online de forma prática, enquanto o pedido é realizado diretamente pelo garçom.'
                            />
                            <FeatureItem name='Menu Online via QRCode' iconType='times'/>
                            <FeatureItem name='Menu Online via QRCode' iconType='check'/>
                            <FeatureItem name='Menu Online via QRCode' iconType='check'/>
                            {/* Delivery */}
                            <HeaderTitle
                                title='Delivery'
                                popoverContent='Centralize os pedidos de delivery com uma interface integrada que recebe pedidos automaticamente da sua página online ou permite a criação manual. Mantenha os clientes informados via WhatsApp, com atualizações desde o preparo até a saída para entrega, garantindo uma experiência transparente e prática.'
                            />
                            <FeatureItem name='Delivery' iconType='times'/>
                            <FeatureItem name='Delivery' iconType='times'/>
                            <FeatureItem name='Delivery' iconType='check'/>
                            {/* Pedidos Online */}
                            <HeaderTitle
                                title='Página de Pedidos Online Integrada ao Delivery'
                                popoverContent='Oferecemos uma página de pedidos online pronta e personalizada no nosso padrão, para que você disponibilize aos seus clientes e facilite a venda dos seus produtos cadastrados no nosso sistema.'
                            />
                            <FeatureItem name='Pedidos Online' iconType='times'/>
                            <FeatureItem name='Pedidos Online' iconType='times'/>
                            <FeatureItem name='Pedidos Online' iconType='check'/>
                            {/* Support */}
                            <div className='px-6 flex flex-col justify-end'>
                                <div className='py-2 text-slate-50 font-medium mt-4'>Suporte</div>
                            </div>
                            <div className='px-6 flex flex-col justify-end'>
                                <div className='py-2 text-slate-50 font-medium mt-4 md:hidden'>Suporte</div>
                            </div>
                            <div className='px-6 flex flex-col justify-end'>
                                <div className='py-2 text-slate-50 font-medium mt-4 md:hidden'>Suporte</div>
                            </div>
                            <div className='px-6 flex flex-col justify-end'>
                                <div className='py-2 text-slate-50 font-medium mt-4 md:hidden'>Suporte</div>
                            </div>
                            {/* Support */}
                            <HeaderTitle
                                title='Suporte Online a Plataforma via WhatsaApp'
                                popoverContent='Conte com suporte especializado com atendimento direto para solucionar dúvidas relacionadas à plataforma. Disponível exclusivamente nos planos Profissional e Ultra. O plano Básico NÃO inclui suporte.'
                            />
                            <FeatureItem name='Suporte Online a Plataforma' iconType='times'/>
                            <FeatureItem name='Suporte Online a Plataforma' iconType='check'/>
                            <FeatureItem name='Suporte Online a Plataforma' iconType='check'/>
                        </div>
                    </div>

                    {/* Mobile Version */}
                    <div className='md:hidden'>
                        {/* Toggle switch */}
                        <div className='text-center mb-8'>
                            <div className='flex flex-col items-center'>
                                <div className='text-sm text-center mb-3'>
                  <span className='text-teal-500'>
                    {getDiscountMessage()}
                  </span>
                                </div>

                                {/* Radio buttons para seleção de período - Mobile */}
                                <div className='flex flex-wrap justify-center gap-2 text-xs'>
                                    <label className='flex items-center cursor-pointer'>
                                        <input
                                            type='radio'
                                            name='period-mobile'
                                            value='monthly'
                                            checked={selectedPeriod === 'monthly'}
                                            onChange={(e) => setSelectedPeriod(e.target.value as Period)}
                                            className='sr-only peer'
                                        />
                                        <div className={`px-2 py-1 rounded-md border transition-colors ${
                                            selectedPeriod === 'monthly'
                                                ? 'bg-purple-500 text-white border-purple-500'
                                                : 'bg-slate-700 text-slate-300 border-slate-600 hover:border-purple-400'
                                        }`}>
                                            Mensal
                                        </div>
                                    </label>

                                    <label className='flex items-center cursor-pointer'>
                                        <input
                                            type='radio'
                                            name='period-mobile'
                                            value='quarterly'
                                            checked={selectedPeriod === 'quarterly'}
                                            onChange={(e) => setSelectedPeriod(e.target.value as Period)}
                                            className='sr-only peer'
                                        />
                                        <div className={`px-2 py-1 rounded-md border transition-colors ${
                                            selectedPeriod === 'quarterly'
                                                ? 'bg-purple-500 text-white border-purple-500'
                                                : 'bg-slate-700 text-slate-300 border-slate-600 hover:border-purple-400'
                                        }`}>
                                            Trimestral
                                        </div>
                                    </label>

                                    <label className='flex items-center cursor-pointer'>
                                        <input
                                            type='radio'
                                            name='period-mobile'
                                            value='semi_annual'
                                            checked={selectedPeriod === 'semi_annual'}
                                            onChange={(e) => setSelectedPeriod(e.target.value as Period)}
                                            className='sr-only peer'
                                        />
                                        <div className={`px-2 py-1 rounded-md border transition-colors ${
                                            selectedPeriod === 'semi_annual'
                                                ? 'bg-purple-500 text-white border-purple-500'
                                                : 'bg-slate-700 text-slate-300 border-slate-600 hover:border-purple-400'
                                        }`}>
                                            Semestral
                                        </div>
                                    </label>

                                    <label className='flex items-center cursor-pointer'>
                                        <input
                                            type='radio'
                                            name='period-mobile'
                                            value='yearly'
                                            checked={selectedPeriod === 'yearly'}
                                            onChange={(e) => setSelectedPeriod(e.target.value as Period)}
                                            className='sr-only peer'
                                        />
                                        <div className={`px-2 py-1 rounded-md border transition-colors ${
                                            selectedPeriod === 'yearly'
                                                ? 'bg-purple-500 text-white border-purple-500'
                                                : 'bg-slate-700 text-slate-300 border-slate-600 hover:border-purple-400'
                                        }`}>
                                            Anual
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Plano Básico - Mobile */}
                        <div className='mb-8 bg-slate-700/20 rounded-t-3xl rounded-b-3xl overflow-hidden'>
                            <div className='px-6 py-6'>
                                <div
                                    className='text-base font-medium bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-purple-200 pb-0.5'>
                                    Plano Básico
                                </div>
                                <div className='mb-1'>
                                    <span className='text-lg font-medium text-slate-500 pr-1'>R$</span>
                                    <span
                                        className='text-3xl font-bold text-slate-50'>{plans.basico[selectedPeriod]}</span>/
                                    <span
                                        className='text-sm text-slate-600 font-medium pl-1'>{getPeriodLabel(selectedPeriod)}</span>
                                </div>
                                <div className='text-slate-500 text-center mb-4'>Para Pequenos Negócios Até 2
                                    Funcionários
                                </div>

                                <button
                                    onClick={() => sendMessageToWhatsApp("basico")}
                                    className='btn-sm text-slate-900 bg-gradient-to-r from-white/80 via-white to-white/80 hover:bg-white w-full transition-transform duration-150 ease-in-out group rounded-ss-lg hover:scale-105'
                                >
                                    Assinar Básico
                                    <span
                                        className='tracking-normal text-purple-500 group-hover:translate-x-0.5 transition-transform duration-150 ease-in-out ml-1'>
                    ➤
                  </span>
                                </button>
                            </div>

                            <div className='py-4 text-slate-50 font-medium px-6 border-t border-slate-800'>Serviços
                            </div>

                            <MobileFeatureInfo
                                title='Atualizações em Tempo Real'
                                iconType='check'
                                popoverContent='Inclui 2.000 requisições diárias para atualizações em tempo real. Acima desse limite, custos adicionais podem ser aplicados.'
                            />
                            <MobileFeatureInfo
                                title='Vendas PDV'
                                iconType='check'
                                popoverContent='Realize vendas rápidas no balcão com um PDV de interface simples e intuitiva, ideal para agilizar vendas.'
                            />
                            <MobileFeatureInfo
                                title='Estoque'
                                iconType='check'
                                popoverContent='Gerencie seu estoque de forma integrada ao módulo de produtos, permitindo adicionar, remover e ajustar quantidades com facilidade para manter o controle total dos itens.'
                            />
                            <MobileFeatureInfo
                                title='Clientes'
                                iconType='check'
                                popoverContent='Gerencie seus clientes com facilidade, cadastrando-os manualmente ou automaticamente via página de pedidos online, e tenha controle total sobre as informações e histórico de cada cliente.'
                            />
                            <MobileFeatureInfo
                                title='Criação de Produtos Simples'
                                iconType='check'
                                popoverContent='Cadastre produtos definindo preços, categorias e descrições de forma rápida e prática.'
                            />
                            <MobileFeatureInfo
                                title='Criação de Produtos Compostos'
                                iconType='times'
                                popoverContent='Monte combos e kits de produtos de maneira simples e fácil.'
                            />
                            <MobileFeatureInfo
                                title='Comandas'
                                iconType='times'
                                popoverContent='Melhore o atendimento com comandas digitais, eliminando erros e otimizando o fluxo de pedidos tanto no balcão quanto pelos garçons, para uma operação ágil e eficiente.'
                            />
                            <MobileFeatureInfo
                                title='Menu Online via QRCode'
                                iconType='times'
                                popoverContent='Disponibilizamos um menu online via QRCode, integrado às comandas, para que seus clientes visualizem os produtos disponíveis ao escanear o código no estabelecimento. Ideal para ser impresso e fixado nas mesas, permitindo que o cliente confira o cardápio online de forma prática, enquanto o pedido é realizado diretamente pelo garçom.'
                            />
                            <MobileFeatureInfo
                                title='Delivery'
                                iconType='times'
                                popoverContent='Centralize os pedidos de delivery com uma interface integrada que recebe pedidos automaticamente da sua página online ou permite a criação manual. Mantenha os clientes informados via WhatsApp, com atualizações desde o preparo até a saída para entrega, garantindo uma experiência transparente e prática.'
                            />
                            <MobileFeatureInfo
                                title='Pedidos Online'
                                iconType='times'
                                popoverContent='Oferecemos uma página de pedidos online pronta e personalizada no nosso padrão, para que você disponibilize aos seus clientes e facilite a venda dos seus produtos cadastrados no nosso sistema.'
                            />

                            <div
                                className='py-4 text-slate-50 font-medium px-6 mt-4 border-t border-slate-800'>Suporte
                            </div>

                            <MobileFeatureInfo
                                title='Suporte Online a Plataforma'
                                iconType='times'
                                popoverContent='Conte com suporte especializado com atendimento direto para solucionar dúvidas relacionadas à plataforma. Disponível exclusivamente nos planos Profissional e Ultra. O plano Básico NÃO inclui suporte.'
                            />
                        </div>

                        {/* Plano Profissional - Mobile */}
                        <div
                            className='mb-8 bg-slate-700/20 rounded-t-3xl rounded-b-3xl overflow-hidden relative before:absolute before:-inset-px before:rounded-[inherit] before:border-x-2 before:border-y-2 before:border-purple-500 before:-z-10 before:pointer-events-none'>
                            <div className='px-6 py-6'>
                                <div
                                    className='text-base font-medium bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-purple-200 pb-0.5'>
                                    Plano Profissional
                                </div>
                                <div className='mb-1'>
                                    <span className='text-lg font-medium text-slate-500 pr-1'>R$</span>
                                    <span
                                        className='text-3xl font-bold text-slate-50'>{plans.profissional[selectedPeriod]}</span>/
                                    <span
                                        className='text-sm text-slate-600 font-medium pl-1'>{getPeriodLabel(selectedPeriod)}</span>
                                </div>
                                <div className='text-slate-500 text-center mb-4'>Para Negócios Com Alto Volume de
                                    Vendas
                                </div>

                                <button
                                    onClick={() => sendMessageToWhatsApp("profissional")}
                                    className='btn-sm text-slate-900 bg-gradient-to-r from-white/80 via-white to-white/80 hover:bg-white w-full transition-transform duration-150 ease-in-out group rounded-ss-lg hover:scale-105'
                                >
                                    Assinar Profissional
                                    <span
                                        className='tracking-normal text-purple-500 group-hover:translate-x-0.5 transition-transform duration-150 ease-in-out ml-1'>
                    ➤
                  </span>
                                </button>
                            </div>

                            <div className='py-4 text-slate-50 font-medium px-6 border-t border-slate-800'>Serviços
                            </div>

                            <MobileFeatureInfo
                                title='Atualizações em Tempo Real'
                                iconType='check'
                                popoverContent='Inclui 2.000 requisições diárias para atualizações em tempo real. Acima desse limite, custos adicionais podem ser aplicados.'
                            />
                            <MobileFeatureInfo
                                title='Vendas PDV'
                                iconType='check'
                                popoverContent='Realize vendas rápidas no balcão com um PDV de interface simples e intuitiva, ideal para agilizar vendas.'
                            />
                            <MobileFeatureInfo
                                title='Estoque'
                                iconType='check'
                                popoverContent='Gerencie seu estoque de forma integrada ao módulo de produtos, permitindo adicionar, remover e ajustar quantidades com facilidade para manter o controle total dos itens.'
                            />
                            <MobileFeatureInfo
                                title='Clientes'
                                iconType='check'
                                popoverContent='Gerencie seus clientes com facilidade, cadastrando-os manualmente ou automaticamente via página de pedidos online, e tenha controle total sobre as informações e histórico de cada cliente.'
                            />
                            <MobileFeatureInfo
                                title='Criação de Produtos Simples'
                                iconType='check'
                                popoverContent='Cadastre produtos definindo preços, categorias e descrições de forma rápida e prática.'
                            />
                            <MobileFeatureInfo
                                title='Criação de Produtos Compostos'
                                iconType='check'
                                popoverContent='Monte combos e kits de produtos de maneira simples e fácil.'
                            />
                            <MobileFeatureInfo
                                title='Comandas'
                                iconType='check'
                                popoverContent='Melhore o atendimento com comandas digitais, eliminando erros e otimizando o fluxo de pedidos tanto no balcão quanto pelos garçons, para uma operação ágil e eficiente.'
                            />
                            <MobileFeatureInfo
                                title='Menu Online via QRCode'
                                iconType='check'
                                popoverContent='Disponibilizamos um menu online via QRCode, integrado às comandas, para que seus clientes visualizem os produtos disponíveis ao escanear o código no estabelecimento. Ideal para ser impresso e fixado nas mesas, permitindo que o cliente confira o cardápio online de forma prática, enquanto o pedido é realizado diretamente pelo garçom.'
                            />
                            <MobileFeatureInfo
                                title='Delivery'
                                iconType='times'
                                popoverContent='Centralize os pedidos de delivery com uma interface integrada que recebe pedidos automaticamente da sua página online ou permite a criação manual. Mantenha os clientes informados via WhatsApp, com atualizações desde o preparo até a saída para entrega, garantindo uma experiência transparente e prática.'
                            />
                            <MobileFeatureInfo
                                title='Pedidos Online'
                                iconType='times'
                                popoverContent='Oferecemos uma página de pedidos online pronta e personalizada no nosso padrão, para que você disponibilize aos seus clientes e facilite a venda dos seus produtos cadastrados no nosso sistema.'
                            />

                            <div
                                className='py-4 text-slate-50 font-medium px-6 mt-4 border-t border-slate-800'>Suporte
                            </div>

                            <MobileFeatureInfo
                                title='Suporte Online a Plataforma'
                                iconType='check'
                                popoverContent='Conte com suporte especializado com atendimento direto para solucionar dúvidas relacionadas à plataforma. Disponível exclusivamente nos planos Profissional e Ultra. O plano Básico NÃO inclui suporte.'
                            />
                        </div>

                        {/* Plano Ultra - Mobile */}
                        <div className='mb-8 bg-slate-700/20 rounded-t-3xl rounded-b-3xl overflow-hidden'>
                            <div className='px-6 py-6'>
                                <div
                                    className='text-base font-medium bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-purple-200 pb-0.5'>
                                    Plano Ultra
                                </div>
                                <div className='mb-1'>
                                    <span className='text-lg font-medium text-slate-500 pr-1'>R$</span>
                                    <span
                                        className='text-3xl font-bold text-slate-50'>{plans.ultra[selectedPeriod]}</span>/
                                    <span
                                        className='text-sm text-slate-600 font-medium pl-1'>{getPeriodLabel(selectedPeriod)}</span>
                                </div>
                                <div className='text-slate-500 text-center mb-4'>
                                    Para Grandes Empresas Operando em Múltiplas Frentes
                                </div>

                                <button
                                    onClick={() => sendMessageToWhatsApp("ultra")}
                                    className='btn-sm text-slate-900 bg-gradient-to-r from-white/80 via-white to-white/80 hover:bg-white w-full transition-transform duration-150 ease-in-out group rounded-ss-lg hover:scale-105'
                                >
                                    Assinar Ultra
                                    <span
                                        className='tracking-normal text-purple-500 group-hover:translate-x-0.5 transition-transform duration-150 ease-in-out ml-1'>
                    ➤
                  </span>
                                </button>
                            </div>

                            <div className='py-4 text-slate-50 font-medium px-6 border-t border-slate-800'>Serviços
                            </div>

                            <MobileFeatureInfo
                                title='Atualizações em Tempo Real'
                                iconType='check'
                                popoverContent='Inclui 2.000 requisições diárias para atualizações em tempo real. Acima desse limite, custos adicionais podem ser aplicados.'
                            />
                            <MobileFeatureInfo
                                title='Vendas PDV'
                                iconType='check'
                                popoverContent='Realize vendas rápidas no balcão com um PDV de interface simples e intuitiva, ideal para agilizar vendas.'
                            />
                            <MobileFeatureInfo
                                title='Estoque'
                                iconType='check'
                                popoverContent='Gerencie seu estoque de forma integrada ao módulo de produtos, permitindo adicionar, remover e ajustar quantidades com facilidade para manter o controle total dos itens.'
                            />
                            <MobileFeatureInfo
                                title='Clientes'
                                iconType='check'
                                popoverContent='Gerencie seus clientes com facilidade, cadastrando-os manualmente ou automaticamente via página de pedidos online, e tenha controle total sobre as informações e histórico de cada cliente.'
                            />
                            <MobileFeatureInfo
                                title='Criação de Produtos Simples'
                                iconType='check'
                                popoverContent='Cadastre produtos definindo preços, categorias e descrições de forma rápida e prática.'
                            />
                            <MobileFeatureInfo
                                title='Criação de Produtos Compostos'
                                iconType='check'
                                popoverContent='Monte combos e kits de produtos de maneira simples e fácil.'
                            />
                            <MobileFeatureInfo
                                title='Comandas'
                                iconType='check'
                                popoverContent='Melhore o atendimento com comandas digitais, eliminando erros e otimizando o fluxo de pedidos tanto no balcão quanto pelos garçons, para uma operação ágil e eficiente.'
                            />
                            <MobileFeatureInfo
                                title='Menu Online via QRCode'
                                iconType='check'
                                popoverContent='Disponibilizamos um menu online via QRCode, integrado às comandas, para que seus clientes visualizem os produtos disponíveis ao escanear o código no estabelecimento. Ideal para ser impresso e fixado nas mesas, permitindo que o cliente confira o cardápio online de forma prática, enquanto o pedido é realizado diretamente pelo garçom.'
                            />
                            <MobileFeatureInfo
                                title='Delivery'
                                iconType='check'
                                popoverContent='Centralize os pedidos de delivery com uma interface integrada que recebe pedidos automaticamente da sua página online ou permite a criação manual. Mantenha os clientes informados via WhatsApp, com atualizações desde o preparo até a saída para entrega, garantindo uma experiência transparente e prática.'
                            />
                            <MobileFeatureInfo
                                title='Pedidos Online'
                                iconType='check'
                                popoverContent='Oferecemos uma página de pedidos online pronta e personalizada no nosso padrão, para que você disponibilize aos seus clientes e facilite a venda dos seus produtos cadastrados no nosso sistema.'
                            />

                            <div
                                className='py-4 text-slate-50 font-medium px-6 mt-4 border-t border-slate-800'>Suporte
                            </div>

                            <MobileFeatureInfo
                                title='Suporte Online a Plataforma'
                                iconType='check'
                                popoverContent='Conte com suporte especializado com atendimento direto para solucionar dúvidas relacionadas à plataforma. Disponível exclusivamente nos planos Profissional e Ultra. O plano Básico NÃO inclui suporte.'
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}